/**
 * 自繪 canvas 圖表（RPT-001，批次 33，Gitea issue #34／B14-1 結案）。
 *
 * 裁決（使用者 2026-09-29）：**自繪 canvas 零依賴**——不引 Chart.js（§3.2 不加套件，
 * B19-6 LightCalendar 自製月曆前例），用 canvas 2D API 手畫 pie／bar／line 三型，
 * 涵蓋 10 個報告頁型的 19 個 canvas（三角色去重後）。
 *
 * 【鏡像目標】legacy＝Chart.js 2.9.3（webjars；courseStaticList.jsp:22 等各頁引入）。
 * canvas 元素 tag 與 legacy 相同（structure-diff 的 tag 層配對；width/height 屬性不在
 * normalize allowlist、id 已丟——比對面＝tag 存在性）。圖表內容（JS 繪製）本來就不進 DOM，
 * raw-dom 比對不受影響；視覺上本元件重現 Chart.js 的版面（title 頂部、legend 頂部、
 * pie 百分比 dataset、bar 填色 alpha 0.5、line 多 dataset 不填面）。
 *
 * 【quirk 照實】（見各頁掛接處註解）
 * - courseStaticList.jsp:267／LoginStatic.jsp:159 的第二面 pie 用 `dataColor`（第一面的
 *   顏色陣列）而非 `dataColor2`——掛接層照實傳同一陣列。
 * - classFormStatic.jsp:87／planFormStatic.jsp:171 的「最高」線色 `chartColors.skyblue`
 *   在色票中不存在（utils.js:3-10 僅 7 色）＝undefined → Chart.js 預設灰，照實以灰畫。
 * - planFormStatic2.jsp:170 的 pie 顏色取迴圈殘值 `colorNames[cx]`（cx 已超界）＝
 *   undefined×N → Chart.js 預設灰，照實全灰。
 *
 * 【jsdom】單元測試環境沒有 2D context（getContext 回 null）——draw 都要 guard，
 * 測試只斷言 canvas 元素/ui-id 存在。
 */
import { useEffect, useRef, type ReactElement, type RefObject } from 'react';

/** legacy 色票：js/Chart.js/samples/utils.js:3-10 的 `window.chartColors`（僅 7 色）。 */
export const CHART_COLORS = {
  red: 'rgb(255, 99, 132)',
  orange: 'rgb(255, 159, 64)',
  yellow: 'rgb(255, 205, 86)',
  green: 'rgb(75, 192, 192)',
  blue: 'rgb(54, 162, 235)',
  purple: 'rgb(153, 102, 255)',
  grey: 'rgb(201, 203, 207)',
} as const;

export type ChartColorName = keyof typeof CHART_COLORS;

/** legacy `colorNames`＝Object.keys(chartColors)（planFormStatic2.jsp:26 等）。 */
export const CHART_COLOR_NAMES: readonly string[] = Object.keys(CHART_COLORS);

/** Chart.js 對 undefined 色的預設（2.9.3）：rgba(0,0,0,0.1) 灰。quirk 的 skyblue/殘值迴圈落到這。 */
export const CHART_FALLBACK_COLOR = 'rgba(0, 0, 0, 0.1)';

/** 依 legacy 的 colorNames[cc] 取色：超界（undefined）＝Chart.js 預設灰（quirk 照實）。 */
export function chartColorAt(index: number): string {
  const name = CHART_COLOR_NAMES[index];
  return name === undefined ? CHART_FALLBACK_COLOR : CHART_COLORS[name as ChartColorName];
}

/** alpha(0.5) 的 rgbString（Chart.helpers.color(...).alpha(0.5).rgbString() 鏡像）。 */
export function withAlpha(rgb: string, alpha = 0.5): string {
  const m = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (m === null) return CHART_FALLBACK_COLOR;
  return `rgba(${m[1]}, ${m[2]}, ${m[3]}, ${alpha})`;
}

const CANVAS_CSS_W = 900;
const CANVAS_H = 450;
const FONT = "12px 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif";
const TITLE_FONT = "16px 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif";

interface ChartCanvasProps {
  /** legacy canvas 的 id 僅供除錯顯示；data-ui-id 才是本專案的識別。 */
  readonly uiId: string;
  /** Chart.js options.title.text（display:true）。 */
  readonly title: string;
}

/** 共通繪製骨架：DPR 設定＋guard＋title/legend 版面。
 *  批次 47 uiId 衛生：改 ref 直取本元件渲染的 canvas——原 querySelector 用 canvas[data-ui-id=${uiId}]
 *  引號包值的模板字串查詢，會被 check-ui-ids.ts 的 STATIC_ID_ATTR 當字面 id 擷取（連 ${uiId}
 *  一起）驗 kebab-case；ref 直取語意等價（同 component 渲染的元素）且免 selector 字串
 *  （data-ui-id 屬性照舊輸出，測試仍以屬性查詢斷言）。 */
function useChartDraw(canvasRef: RefObject<HTMLCanvasElement | null>, draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void): void {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return; // jsdom：無 2D context，安全跳過
    const dpr = 1;
    canvas.width = CANVAS_CSS_W * dpr;
    canvas.height = CANVAS_H * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, CANVAS_CSS_W, CANVAS_H);
    draw(ctx, CANVAS_CSS_W, CANVAS_H);
  });
}

/** 畫 title（頂部中央）＋legend（title 下方一列，色塊＋文字），回傳內容區的 y 起點。 */
function drawTitleAndLegend(
  ctx: CanvasRenderingContext2D,
  title: string,
  legend: readonly { readonly label: string; readonly color: string }[],
): number {
  ctx.fillStyle = '#666666';
  ctx.font = TITLE_FONT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(title, CANVAS_CSS_W / 2, 8);
  ctx.font = FONT;
  ctx.textAlign = 'left';
  // legend 一列（置中）：色塊 12×12＋label
  const items = legend.map((l) => ({ ...l, textW: ctx.measureText(l.label).width }));
  const totalW =
    items.reduce((sum, l) => sum + 16 + l.textW + 12, 0) - (items.length > 0 ? 12 : 0);
  let x = (CANVAS_CSS_W - totalW) / 2;
  const ly = 34;
  for (const l of items) {
    ctx.fillStyle = l.color;
    ctx.fillRect(x, ly + 2, 12, 12);
    ctx.fillStyle = '#666666';
    ctx.fillText(l.label, x + 16, ly + 2);
    x += 16 + l.textW + 12;
  }
  return ly + 20;
}

/** 圓餅圖（Chart.js type:'pie'：值不顯示、面積比例；dataset 值＝百分比字串的頁照實只影響顯示數值）。 */
export function PieCanvas(props: ChartCanvasProps & {
  readonly slices: readonly { readonly label: string; readonly value: number; readonly color: string }[];
}): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useChartDraw(canvasRef, (ctx) => {
    const top = drawTitleAndLegend(
      ctx,
      props.title,
      props.slices.map((s) => ({ label: s.label, color: s.color })),
    );
    const total = props.slices.reduce((sum, s) => sum + s.value, 0) || 1;
    const cx = CANVAS_CSS_W / 2;
    const cy = top + (CANVAS_H - top) / 2;
    const r = Math.min(CANVAS_CSS_W, CANVAS_H - top) / 2 - 30;
    let angle = -Math.PI / 2;
    for (const s of props.slices) {
      const sweep = (s.value / total) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, angle, angle + sweep);
      ctx.closePath();
      ctx.fillStyle = s.color;
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.stroke();
      angle += sweep;
    }
  });
  return <canvas ref={canvasRef} data-ui-id={props.uiId} className="mx-auto block" />;
}

/** 長條圖（Chart.js type:'bar'；填色 alpha 0.5＋border 實色）。 */
export function BarCanvas(props: ChartCanvasProps & {
  readonly labels: readonly string[];
  readonly datasets: readonly {
    readonly label: string;
    readonly data: readonly number[];
    readonly color: string;
  }[];
  readonly xAxisLabel?: string;
  readonly yAxisLabel?: string;
}): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useChartDraw(canvasRef, (ctx) => {
    const top = drawTitleAndLegend(
      ctx,
      props.title,
      props.datasets.map((d) => ({ label: d.label, color: withAlpha(d.color) })),
    );
    const left = 50;
    const right = CANVAS_CSS_W - 10;
    const plotTop = top + (props.xAxisLabel === undefined ? 0 : 6);
    const plotBottom = CANVAS_H - 34;
    const maxV = Math.max(1, ...props.datasets.flatMap((d) => [...d.data]));
    ctx.strokeStyle = '#dddddd';
    ctx.strokeRect(left, plotTop, right - left, plotBottom - plotTop);
    const n = props.labels.length || 1;
    const groupW = (right - left) / n;
    const barW = groupW / props.datasets.length;
    props.datasets.forEach((ds, di) => {
      ds.data.forEach((v, i) => {
        const h = ((v ?? 0) / maxV) * (plotBottom - plotTop);
        const x = left + i * groupW + di * barW;
        ctx.fillStyle = withAlpha(ds.color);
        ctx.fillRect(x + 2, plotBottom - h, Math.max(1, barW - 4), h);
        ctx.strokeStyle = ds.color;
        ctx.strokeRect(x + 2, plotBottom - h, Math.max(1, barW - 4), h);
      });
    });
    // x 軸標籤＋軸標題
    ctx.fillStyle = '#666666';
    ctx.textAlign = 'center';
    props.labels.forEach((l, i) => {
      ctx.fillText(String(l), left + i * groupW + groupW / 2, plotBottom + 6);
    });
    if (props.xAxisLabel !== undefined) {
      ctx.fillText(props.xAxisLabel, (left + right) / 2, CANVAS_H - 12);
    }
    ctx.textAlign = 'right';
    if (props.yAxisLabel !== undefined) {
      ctx.fillText(props.yAxisLabel, left - 4, plotTop - 4);
    }
  });
  return <canvas ref={canvasRef} data-ui-id={props.uiId} className="mx-auto block" />;
}

/** 折線圖（Chart.js type:'line'，fill:false 多 dataset）。 */
export function LineCanvas(props: ChartCanvasProps & {
  readonly labels: readonly string[];
  readonly datasets: readonly {
    readonly label: string;
    readonly data: readonly number[];
    /** legacy datasets 的 borderColor；undefined（skyblue quirk）＝Chart.js 預設灰照實。 */
    readonly color: string;
  }[];
  readonly xAxisLabel?: string;
  readonly yAxisLabel?: string;
}): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useChartDraw(canvasRef, (ctx) => {
    const top = drawTitleAndLegend(
      ctx,
      props.title,
      props.datasets.map((d) => ({ label: d.label, color: d.color })),
    );
    const left = 50;
    const right = CANVAS_CSS_W - 10;
    const plotTop = top + 6;
    const plotBottom = CANVAS_H - 34;
    const maxV = Math.max(1, ...props.datasets.flatMap((d) => [...d.data]));
    ctx.strokeStyle = '#dddddd';
    ctx.strokeRect(left, plotTop, right - left, plotBottom - plotTop);
    const n = props.labels.length || 1;
    const step = (right - left) / Math.max(1, n - 1);
    for (const ds of props.datasets) {
      ctx.beginPath();
      ctx.strokeStyle = ds.color;
      ds.data.forEach((v, i) => {
        const x = left + i * step;
        const y = plotBottom - ((v ?? 0) / maxV) * (plotBottom - plotTop);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }
    ctx.fillStyle = '#666666';
    ctx.textAlign = 'center';
    props.labels.forEach((l, i) => {
      ctx.fillText(String(l), left + i * step, plotBottom + 6);
    });
    if (props.xAxisLabel !== undefined) {
      ctx.fillText(props.xAxisLabel, (left + right) / 2, CANVAS_H - 12);
    }
    ctx.textAlign = 'right';
    if (props.yAxisLabel !== undefined) {
      ctx.fillText(props.yAxisLabel, left - 4, plotTop - 4);
    }
  });
  return <canvas ref={canvasRef} data-ui-id={props.uiId} className="mx-auto block" />;
}
